import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const PaymentMethodsPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [selectedMethod, setSelectedMethod] = useState("");
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [coOwners, setCoOwners] = useState([]);
  const [totalAmount, setTotalAmount] = useState(5000);
  const lawsuitAmount = parseInt(Cookies.get("lawsuit_amount") || "5000", 10);

  // 사용자 정보 가져오기
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // 공동신청자 정보 및 결제 금액 계산
  useEffect(() => {
    try {
      // 신청 유형 확인
      const applicationType = Cookies.get("applicationType") || "self";

      // 공동소유자 정보 가져오기
      if (applicationType === "representative") {
        const coOwnersJson = Cookies.get("coOwners");
        if (coOwnersJson) {
          const parsedCoOwners = JSON.parse(coOwnersJson);
          setCoOwners(parsedCoOwners);

          // 본인 포함 총 인원 계산 (본인 + 공동소유자 수)
          const totalPeople = 1 + parsedCoOwners.length;

          // 총 결제 금액 계산 (인당 5000원)
          const calculatedAmount = totalPeople * 5000;
          setTotalAmount(calculatedAmount);

          // 쿠키에 업데이트된 금액 저장
          Cookies.set("lawsuit_amount", calculatedAmount, {
            expires: 7,
            path: "/",
          });
        }
      } else {
        // 본인만 있는 경우 5000원 설정
        setTotalAmount(5000);
        Cookies.set("lawsuit_amount", 5000, {
          expires: 7,
          path: "/",
        });
      }
    } catch (e) {
      console.error("결제 금액 계산 오류:", e);
      // 오류 발생 시 기본값으로 5000원 설정
      setTotalAmount(5000);
    }
  }, []);

  // 뒤로 가기
  const handleBack = () => {
    navigate(-1);
  };

  // 결제 방법 선택 처리
  const handleSelectPaymentMethod = (method) => {
    setSelectedMethod(method);
  };

  // 결제 진행 처리
  const handleContinue = () => {
    if (!selectedMethod) {
      return;
    }

    // 선택한 결제 방법에 따라 적절한 페이지로 이동
    if (selectedMethod === "kakaopay") {
      navigate("/payment"); // 기존 카카오페이 결제 페이지
    } else if (selectedMethod === "creditcard") {
      navigate("/payment/creditcard"); // 신용카드 결제 페이지 (아직 구현 필요)
    } else if (selectedMethod === "banktransfer") {
      navigate("/payment/banktransfer"); // 계좌이체 결제 페이지 (아직 구현 필요)
    } else if (selectedMethod === "mobile") {
      navigate("/payment/mobile"); // 휴대폰 소액결제 페이지 (아직 구현 필요)
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 로딩 표시
  if (isLoading) {
    return (
      <PageBackground>
        <Container>
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>결제 방법 선택</HeaderTitle>
          </Header>
          <Content>
            <LoadingContainer>
              <Spinner />
              <LoadingMessage>
                사용자 정보를 불러오는 중입니다...
              </LoadingMessage>
            </LoadingContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <PageBackground>
        <Container>
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>결제 방법 선택</HeaderTitle>
          </Header>
          <Content>
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorTitle>오류가 발생했습니다</ErrorTitle>
              <ErrorDescription>{error}</ErrorDescription>
              <RetryButton onClick={() => window.location.reload()}>
                다시 시도하기
              </RetryButton>
            </ErrorContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Header>
          <BackButton onClick={handleBack}>
            <BackIcon>&#10094;</BackIcon>
          </BackButton>
          <HeaderTitle>결제 방법 선택</HeaderTitle>
        </Header>
        <Content variants={contentVariants} initial="initial" animate="in">
          <ContentSection>
            <TitleSection>
              <Title>결제 방법 선택</Title>
              <SubTitle>결제를 진행할 방법을 선택해주세요</SubTitle>
            </TitleSection>

            {userData && (
              <UserInfoBox>
                <BoxTitle>결제자 정보</BoxTitle>
                <DetailRow>
                  <DetailLabel>이름</DetailLabel>
                  <DetailValue>{userData.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>연락처</DetailLabel>
                  <DetailValue>{userData.phoneNumber}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>이메일</DetailLabel>
                  <DetailValue>{userData.email}</DetailValue>
                </DetailRow>
              </UserInfoBox>
            )}

            <PaymentDetailsBox>
              <BoxTitle>결제 금액 상세</BoxTitle>
              <DetailRow>
                <DetailLabel>접수비</DetailLabel>
                <DetailValue>5,000원 / 인</DetailValue>
              </DetailRow>
              {coOwners.length > 0 && (
                <DetailRow>
                  <DetailLabel>신청 인원</DetailLabel>
                  <DetailValue>
                    본인 + 공동신청자 {coOwners.length}명 = 총{" "}
                    {coOwners.length + 1}명
                  </DetailValue>
                </DetailRow>
              )}
              {coOwners.length === 0 && (
                <DetailRow>
                  <DetailLabel>신청 인원</DetailLabel>
                  <DetailValue>본인 = 총 1명</DetailValue>
                </DetailRow>
              )}
              <Divider />
              <TotalRow>
                <TotalLabel>총 결제 금액</TotalLabel>
                <TotalValue>{totalAmount.toLocaleString()}원</TotalValue>
              </TotalRow>
            </PaymentDetailsBox>

            <PaymentMethodSection>
              <SectionLabel>결제수단 선택</SectionLabel>

              {/* 카카오페이 */}
              <PaymentMethodCard
                isSelected={selectedMethod === "kakaopay"}
                onClick={() => handleSelectPaymentMethod("kakaopay")}
              >
                <PaymentMethodRow>
                  <PaymentIcon src="/png/kakaopayIcon.png" alt="카카오페이" />
                  <div>
                    <PaymentTitle>카카오페이</PaymentTitle>
                    <PaymentDescription>
                      간편하고 안전한 모바일 결제
                    </PaymentDescription>
                  </div>
                  <RadioButton isSelected={selectedMethod === "kakaopay"} />
                </PaymentMethodRow>
              </PaymentMethodCard>

              {/* 신용카드 */}
              <PaymentMethodCard
                isSelected={selectedMethod === "creditcard"}
                onClick={() => handleSelectPaymentMethod("creditcard")}
              >
                <PaymentMethodRow>
                  <PaymentIcon src="/png/creditcardIcon.png" alt="신용카드" />
                  <div>
                    <PaymentTitle>신용카드</PaymentTitle>
                    <PaymentDescription>
                      모든 신용/체크카드 결제 가능
                    </PaymentDescription>
                  </div>
                  <RadioButton isSelected={selectedMethod === "creditcard"} />
                </PaymentMethodRow>
              </PaymentMethodCard>

              {/* 계좌이체 */}
              {/* <PaymentMethodCard
                isSelected={selectedMethod === "mobile"}
                onClick={() => handleSelectPaymentMethod("mobile")}
              >
                <PaymentMethodRow>
                  <PaymentIcon src="/png/banktransferIcon.png" alt="계좌이체" />
                  <div>
                    <PaymentTitle>휴대폰 소액결제</PaymentTitle>
                    <PaymentDescription>휴대폰 소액결제</PaymentDescription>
                  </div>
                  <RadioButton isSelected={selectedMethod === "mobile"} />
                </PaymentMethodRow>
              </PaymentMethodCard> */}
              {/* 계좌이체 */}
              {/* <PaymentMethodCard
                isSelected={selectedMethod === "banktransfer"}
                onClick={() => handleSelectPaymentMethod("banktransfer")}
              >
                <PaymentMethodRow>
                  <PaymentIcon src="/png/accountTransfer.png" alt="계좌이체" />
                  <div>
                    <PaymentTitle>계좌이체</PaymentTitle>
                    <PaymentDescription>실시간 계좌이체</PaymentDescription>
                  </div>
                  <RadioButton isSelected={selectedMethod === "banktransfer"} />
                </PaymentMethodRow>
              </PaymentMethodCard> */}
            </PaymentMethodSection>

            <ButtonContainer>
              <ContinueButton
                onClick={handleContinue}
                disabled={!selectedMethod}
              >
                결제 진행하기
              </ContinueButton>
            </ButtonContainer>
          </ContentSection>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Header = styled.div`
  width: 100%;
  padding: 16px 0;
  text-align: center;
  position: relative;
  border-bottom: 1px solid #eee;
`;

const BackButton = styled.button`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackIcon = styled.span`
  font-size: 20px;
  color: #333;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 500px;
`;

const TitleSection = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px 0;
`;

const SubTitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const UserInfoBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #333;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const DetailLabel = styled.span`
  color: #666;
`;

const DetailValue = styled.span`
  font-weight: 500;
  color: #333;
`;

const PaymentDetailsBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 15px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: #333;
`;

const PaymentMethodSection = styled.div`
  margin-bottom: 32px;
`;

const SectionLabel = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
`;

const PaymentMethodCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 12px;
  background-color: white;
  border: 1px solid ${(props) => (props.isSelected ? "#0075FF" : "#e0e0e0")};
  box-shadow: ${(props) =>
    props.isSelected
      ? "0 2px 8px rgba(0, 117, 255, 0.15)"
      : "0 2px 6px rgba(0, 0, 0, 0.05)"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0075ff;
    box-shadow: 0 2px 8px rgba(0, 117, 255, 0.15);
  }
`;

const PaymentMethodRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const PaymentIcon = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 12px;
`;

const PaymentTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const PaymentDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const RadioButton = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? "#0075FF" : "#ccc")};
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:after {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) =>
      props.isSelected ? "#0075FF" : "transparent"};
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  position: ${(props) => (props.fixed ? "fixed" : "relative")};
  bottom: ${(props) => (props.fixed ? "0" : "auto")};
  left: 0;
  padding: ${(props) => (props.fixed ? "16px" : "0")};
  background-color: ${(props) => (props.fixed ? "#fff" : "transparent")};
  box-shadow: ${(props) =>
    props.fixed ? "0 -2px 10px rgba(0, 0, 0, 0.1)" : "none"};
`;

const ContinueButton = styled.button`
  width: 100%;
  height: 56px;
  border-radius: 8px;
  background-color: ${(props) => (props.disabled ? "#ccc" : "#0075FF")};
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  transition: background-color 0.2s ease;
`;

// 로딩 관련 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 117, 255, 0.1);
  border-radius: 50%;
  border-left-color: #0075ff;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingMessage = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

// 에러 관련 컴포넌트
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  background-color: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
`;

export default PaymentMethodsPage;
